import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { T } from "@/lib/testIds";
import { ArrowLeft, ArrowRight, CheckCircle2, GraduationCap, Download } from "lucide-react";
import { toast } from "sonner";

// Slug-driven downloadable resources block. Extend this map to add more
// course-specific attachments. Files must live under /frontend/public/.
const COURSE_RESOURCES = {
  "referrals-sci-gateway": [
    {
      title: "Referrals — to do vs done (Lesson 6 tracker)",
      description:
        "Simple, practice-visible spreadsheet for the promise-made-in-consultation stage. Every referral a clinician promises goes on the same day; a row is only marked 'Done' when SCI Gateway shows Submitted. Auto-flags overdue rows RED and drafted-but-not-sent rows AMBER. Deliberately EMR-agnostic — survives the EMIS PCS → Vision migration.",
      href: "/calside-referrals-todo-vs-done-template.xlsx",
      filename: "calside-referrals-todo-vs-done-template.xlsx",
    },
    {
      title: "Referral log — full lifecycle template",
      description:
        "Ready-to-use .xlsx with a Referral log, a live USoC tracker, and a monthly summary sheet. Auto-flags overdue chases and USoC breaches. Drop it on the practice shared drive and start using today.",
      href: "/calside-referral-log-template.xlsx",
      filename: "calside-referral-log-template.xlsx",
    },
  ],
};

function LessonResources({ slug }) {
  const resources = COURSE_RESOURCES[slug];
  if (!resources || resources.length === 0) return null;
  return (
    <div
      className="mt-10 rounded-2xl border border-brand-linen bg-brand-linen/40 p-6"
      data-testid="lesson-resources"
    >
      <div className="text-[11px] uppercase tracking-widest text-brand-muted mb-3">
        Downloadable resources
      </div>
      <div className="space-y-4">
        {resources.map((r) => (
          <a
            key={r.href}
            href={r.href}
            download={r.filename}
            data-testid={`lesson-resource-${r.filename}`}
            className="group flex items-start gap-4 rounded-xl bg-white border border-brand-linen p-5 hover:border-brand-secondary transition-colors"
          >
            <div className="mt-1 shrink-0 rounded-lg bg-brand-primary/10 p-2.5 text-brand-primary group-hover:bg-brand-primary/15">
              <Download className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="font-serif text-lg text-brand-ink">{r.title}</div>
              <div className="text-sm text-brand-body mt-1">{r.description}</div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function formatContent(text) {
  const paragraphs = text.split(/\n\n+/);
  return paragraphs.map((p, i) => {
    const parts = p.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean);
    return (
      <p key={i}>
        {parts.map((seg, j) => {
          if (seg.startsWith("**") && seg.endsWith("**"))
            return <strong key={j}>{seg.slice(2, -2)}</strong>;
          if (seg.startsWith("*") && seg.endsWith("*"))
            return <em key={j}>{seg.slice(1, -1)}</em>;
          return <React.Fragment key={j}>{seg}</React.Fragment>;
        })}
      </p>
    );
  });
}

export default function Lesson() {
  const { slug, order } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const orderNum = parseInt(order, 10);

  useEffect(() => {
    (async () => {
      const results = await Promise.allSettled([
        api.get(`/courses/${slug}/lessons/${orderNum}`),
        api.get(`/progress/${slug}`),
      ]);
      if (results[0].status === "fulfilled") setData(results[0].value.data);
      if (results[1].status === "fulfilled") setProgress(results[1].value.data);
      else setProgress({ completed_lessons: [] });
    })();
  }, [slug, orderNum]);

  if (!data) return <Layout><div className="text-brand-muted">Loading lesson…</div></Layout>;

  const completed = new Set(progress?.completed_lessons || []);
  const isDone = completed.has(String(orderNum));
  const isLast = orderNum >= data.total;

  const markComplete = async () => {
    setBusy(true);
    try {
      const { data: p } = await api.post(`/progress/${slug}/complete-lesson`, { lesson_id: String(orderNum) });
      setProgress(p);
      toast.success("Lesson complete");
    } catch (e) {
      toast.error("Could not save progress");
    } finally {
      setBusy(false);
    }
  };

  const goNext = async () => {
    if (!isDone) await markComplete();
    if (isLast) navigate(`/courses/${slug}/quiz`);
    else navigate(`/courses/${slug}/lessons/${orderNum + 1}`);
  };

  return (
    <Layout>
      <Link to={`/courses/${slug}`} data-testid={T.lessonBack} className="inline-flex items-center gap-2 text-sm text-brand-secondary hover:text-brand-ink">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to course
      </Link>

      <motion.article initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl mx-auto mt-6">
        <div className="text-xs uppercase tracking-[0.28em] text-brand-muted">
          Lesson {orderNum} of {data.total} · {data.course_title}
          {(() => {
            const chars = (data.lesson.content || "").length + (data.lesson.summary || "").length;
            // ~250 wpm × ~5 chars/word ≈ 1250 chars/min
            const mins = Math.max(1, Math.ceil(chars / 1250));
            return <span data-testid="lesson-reading-time"> · {mins} min read</span>;
          })()}
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-ink leading-tight mt-3">{data.lesson.title}</h1>
        <div className="mt-3 text-brand-body italic">{data.lesson.summary}</div>

        <div className="mt-8 lesson-prose">{formatContent(data.lesson.content)}</div>

        <LessonResources slug={slug} />

        <div className="mt-14 pt-8 border-t border-brand-linen flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {orderNum > 1 && (
              <button data-testid={T.lessonPrev} onClick={() => navigate(`/courses/${slug}/lessons/${orderNum - 1}`)}
                className="inline-flex items-center gap-2 text-brand-secondary hover:text-brand-ink px-4 py-2">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Previous
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!isDone && (
              <button data-testid={T.lessonComplete} onClick={markComplete} disabled={busy}
                className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-5 py-2.5 hover:bg-brand-linen disabled:opacity-60">
                <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Mark complete
              </button>
            )}
            <button data-testid={T.lessonNext} onClick={goNext} disabled={busy}
              className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-6 py-2.5 hover:bg-brand-inkHover disabled:opacity-60">
              {isLast ? (<><GraduationCap className="w-4 h-4" strokeWidth={1.5} /> Go to quiz</>) : (<>Next lesson <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></>)}
            </button>
          </div>
        </div>
      </motion.article>
    </Layout>
  );
}
